// File: frontend/src/components/entries/PinnedEntryBlock.tsx

import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { RiPushpin2Fill } from "react-icons/ri";
import { EntryModel } from "../../models";
import { SportTypeEnum } from "../../models/enums/SportTypeEnum";
import { VoteTypeEnum } from "../../models/enums/VoteTypeEnum";
import { PinTargetType } from "../../models/PinModel";
import { usePins } from "../../hooks/usePins";
import { EntryRow } from "./EntryRow";
import "./PinnedEntryBlock.css";
import { parseServerDate } from "../../utils/dateUtils";

const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:8080";

// Same raw → EntryModel mapping as the per-domain entry services
const mapEntry = (raw: any): EntryModel => ({
  id:                raw.id,
  content:           raw.content,
  createdAt:         parseServerDate(raw.createdAt),
  editedAt:          raw.editedAt ? parseServerDate(raw.editedAt) : parseServerDate(raw.createdAt),
  userId:            raw.userId,
  username:          raw.username,
  authorRole:        raw.authorRole || "ROOKIE",
  matchId:           raw.matchId ?? null,
  homeTeam:          raw.homeTeam ?? "",
  awayTeam:          raw.awayTeam ?? "",
  matchDate:         raw.matchDate ? new Date(raw.matchDate) : new Date(),
  sportType:         (raw.sportType as SportTypeEnum) || SportTypeEnum.FOOTBALL,
  plusCount:         raw.plusCount  || 0,
  minusCount:        raw.minusCount || 0,
  favCount:          raw.favCount   || 0,
  currentUserRating: raw.currentUserRating as VoteTypeEnum | null ?? null,
  currentUserHasFav: raw.currentUserHasFav || false,
  personId:          raw.personId  ?? null,
  personFullName:    raw.personFullName ?? null,
  personSlug:        raw.personSlug ?? null,
  clubId:            raw.clubId    ?? null,
  clubName:          raw.clubName  ?? null,
  clubSlug:          raw.clubSlug  ?? null,
  teamId:            raw.teamId    ?? null,
  teamName:          raw.teamName  ?? null,
  teamSlug:          raw.teamSlug  ?? null,
  customTopicId:     raw.customTopicId    ?? null,
  customTopicTitle:  raw.customTopicTitle ?? null,
  customTopicSlug:   raw.customTopicSlug  ?? null,
});

interface PinnedEntryBlockProps {
  // Domain + topic the current page is showing
  type: PinTargetType;
  targetId: number | null | undefined;
}

/**
 * Renders the admin-pinned entry of a topic at the very top of the topic page.
 * When the topic has no pinned entry (the common case) nothing is rendered.
 * The entry is fetched by ID so it stays visible regardless of the current
 * entry-list page, sort, or filter.
 */
export const PinnedEntryBlock: React.FC<PinnedEntryBlockProps> = ({ type, targetId }) => {
  const { t } = useTranslation();
  const { getPinnedEntryId } = usePins();
  const [entry, setEntry] = useState<EntryModel | null>(null);

  const pinnedEntryId = targetId != null ? getPinnedEntryId(type, targetId) : null;

  useEffect(() => {
    let cancelled = false;
    if (pinnedEntryId == null) {
      setEntry(null);
      return;
    }
    const token = localStorage.getItem("token");
    const headers: HeadersInit = { "Content-Type": "application/json" };
    if (token) headers["Authorization"] = `Bearer ${token}`;
    fetch(`${BASE_URL}/api/entries/entry/${pinnedEntryId}`, { headers })
      .then((res) => (res.ok ? res.json() : Promise.reject(res.status)))
      .then((raw) => {
        if (!cancelled) setEntry(mapEntry(raw));
      })
      .catch((err) => {
        // Pin display is a progressive enhancement — never break the topic page
        console.error("Failed to load pinned entry:", err);
        if (!cancelled) setEntry(null);
      });
    return () => {
      cancelled = true;
    };
  }, [pinnedEntryId]);

  if (pinnedEntryId == null || !entry) return null;

  return (
    <div className="pinned-entry-block">
      <div className="pinned-entry-block__label">
        <RiPushpin2Fill size={12} aria-hidden="true" />
        <span>{t("pinned_entry_label", "Sabitlenmiş tanım")}</span>
      </div>
      <EntryRow entry={entry} onDelete={() => setEntry(null)} />
    </div>
  );
};

export default PinnedEntryBlock;